import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Search, Calendar, ChevronDown, Check } from 'lucide-react';

// --- Interfaces ---
interface ClientData {
  name: string;
  relationship?: string;
  contact_number?: string;
  email?: string;
}

interface Patient {
  id: number;
  patient_name: string;
  age: string | number;
  sex: string;
  clients_info: ClientData[];
  client_name_display: string; 
  case_manager: string;
  status: string;
  case_start_date: string;
}

const getClientName = (patient: any) => {
    if (!patient) return 'Direct Client';
    if (patient.client_name) return patient.client_name;
    if (patient.patient_clients && patient.patient_clients.length > 0) {
        return patient.patient_clients[0].client_name || patient.patient_clients[0].name || 'Direct Client';
    }
    if (patient.clients_info && patient.clients_info.length > 0) {
        return patient.clients_info[0].client_name || patient.clients_info[0].name || 'Direct Client';
    }
    return 'Direct Client';
};

const CaseSheetIndex: React.FC = () => {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
  
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoverRowId, setHoverRowId] = useState<number | null>(null);

  // --- Date Selection State ---
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // --- Dropdown UI State (New) ---
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [isYearOpen, setIsYearOpen] = useState(false);
  
  // Refs for click-outside detection
  const monthRef = useRef<HTMLDivElement>(null);
  const yearRef = useRef<HTMLDivElement>(null);

  const months = [
    { val: 1, label: 'January' }, { val: 2, label: 'February' }, { val: 3, label: 'March' },
    { val: 4, label: 'April' }, { val: 5, label: 'May' }, { val: 6, label: 'June' },
    { val: 7, label: 'July' }, { val: 8, label: 'August' }, { val: 9, label: 'September' },
    { val: 10, label: 'October' }, { val: 11, label: 'November' }, { val: 12, label: 'December' }
  ];
  const years = [2023, 2024, 2025, 2026, 2027];

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (monthRef.current && !monthRef.current.contains(event.target as Node)) {
        setIsMonthOpen(false);
      }
      if (yearRef.current && !yearRef.current.contains(event.target as Node)) {
        setIsYearOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/api/patients/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const mappedData = response.data.map((p: any) => ({
          ...p,
          client_name_display: getClientName(p)
      }));

      setPatients(mappedData);
    } catch (err) {
      console.error("Failed to fetch patients");
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToSheet = (patientId: number) => {
    navigate(`/casesheet/${patientId}?month=${selectedMonth}&year=${selectedYear}`);
  };

  const filteredPatients = patients
    // 1. Filter: Searches both Client Name AND Patient Name
    .filter(p => 
      (p.patient_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.client_name_display || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    // 2. Sort: Arranges the list alphabetically by Client Name, ignoring invisible spaces
    .sort((a, b) => {
      const nameA = (a.client_name_display || '').trim().toLowerCase();
      const nameB = (b.client_name_display || '').trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });

  // --- STYLES ---
  const styles: { [key: string]: React.CSSProperties } = {
    page: { padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: '"Inter", sans-serif' },
    
    // Header
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' },
    titleGroup: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
    title: { margin: 0, fontSize: '1.8rem', fontWeight: 700, color: '#0f172a' },
    subtitle: { margin: 0, color: '#64748b', fontSize: '0.9rem' },

    // Controls
    controlsGroup: { display: 'flex', gap: '1rem', alignItems: 'center' },
    
    // --- NEW DROPDOWN STYLES ---
    datePickerContainer: {
        display: 'flex', 
        alignItems: 'center', 
        backgroundColor: 'white', 
        borderRadius: '10px', 
        border: '1px solid #e2e8f0',
        padding: '0.2rem', 
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        position: 'relative'
    },
    pickerBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 0.8rem',
        backgroundColor: 'transparent',
        border: 'none',
        fontSize: '0.9rem',
        fontWeight: 600,
        color: '#334155',
        cursor: 'pointer',
        borderRadius: '8px',
        outline: 'none',
        minWidth: '110px',
        justifyContent: 'space-between'
    },
    divider: {
        width: '1px',
        height: '20px',
        backgroundColor: '#e2e8f0',
        margin: '0 2px'
    },
    dropdownMenu: {
        position: 'absolute',
        top: 'calc(100% + 8px)',
        left: 0,
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        width: '150px',
        maxHeight: '300px',
        overflowY: 'auto',
        zIndex: 50,
        padding: '0.3rem',
    },
    dropdownItem: {
        padding: '0.5rem 0.7rem',
        fontSize: '0.9rem',
        color: '#475569',
        cursor: 'pointer',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'background 0.1s'
    },
    activeItem: {
        backgroundColor: '#eff6ff',
        color: '#2563eb',
        fontWeight: 600
    },

    // Search Styling
    searchContainer: { position: 'relative', display: 'flex', alignItems: 'center' },
    searchInput: {
        padding: '0.6rem 1rem 0.6rem 2.5rem',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        outline: 'none',
        minWidth: '220px',
        fontSize: '0.9rem',
        backgroundColor: 'white',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    },
    searchIcon: { position: 'absolute', left: '10px', color: '#94a3b8' },

    // --- TABLE STYLES ---
    tableCard: { backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #cbd5e1', overflow: 'hidden' },
    tableWrapper: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left', color: '#334155' },
    thead: { backgroundColor: '#e2e8f0', borderBottom: '2px solid #94a3b8' },
    th: { padding: '0.75rem 0.5rem', color: '#334155', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', border: '1px solid #cbd5e1', whiteSpace: 'nowrap' },
    tbody: { backgroundColor: 'white' },
    tr: { transition: 'background-color 0.1s' },
    td: { padding: '0.5rem', border: '1px solid #cbd5e1', verticalAlign: 'middle' },

    statusPill: { display: 'inline-flex', alignItems: 'center', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600, lineHeight: 1, border: '1px solid rgba(0,0,0,0.1)' },
    sheetBtn: { backgroundColor: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', padding: '0.4rem 1rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center', transition: 'all 0.2s', width: '100%' },
    boldText: { fontWeight: 600, color: '#0f172a' },
    subText: { fontSize: '0.75rem', color: '#64748b' }
  };

  const getStatusStyle = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('active')) return { backgroundColor: '#dcfce7', color: '#166534' }; 
    if (s.includes('suspended')) return { backgroundColor: '#ffedd5', color: '#9a3412' }; 
    if (s.includes('stopped') || s.includes('discharged')) return { backgroundColor: '#fee2e2', color: '#991b1b' }; 
    return { backgroundColor: '#f1f5f9', color: '#475569' }; 
  };

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.titleGroup}>
            <h1 style={styles.title}>Case Sheet Index</h1>
            <p style={styles.subtitle}>Select a period and view monthly case sheets.</p>
        </div>
        
        <div style={styles.controlsGroup}>
            
            {/* --- REPLACED: Modern Custom Dropdown --- */}
            <div style={styles.datePickerContainer}>
                
                {/* 1. Month Picker */}
                <div style={{position: 'relative'}} ref={monthRef}>
                    <button 
                        style={styles.pickerBtn} 
                        onClick={() => { setIsMonthOpen(!isMonthOpen); setIsYearOpen(false); }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <div style={{display:'flex', alignItems:'center', gap:'0.5rem'}}>
                           <Calendar size={15} color="#3b82f6" />
                           <span>{months.find(m => m.val === selectedMonth)?.label}</span>
                        </div>
                        <ChevronDown size={14} color="#94a3b8" style={{transform: isMonthOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s'}}/>
                    </button>

                    {isMonthOpen && (
                        <div style={styles.dropdownMenu}>
                            {months.map((m) => {
                                const isActive = m.val === selectedMonth;
                                return (
                                    <div 
                                        key={m.val}
                                        style={{...styles.dropdownItem, ...(isActive ? styles.activeItem : {})}}
                                        onClick={() => { setSelectedMonth(m.val); setIsMonthOpen(false); }}
                                        onMouseEnter={(e) => !isActive && (e.currentTarget.style.backgroundColor = '#f8fafc')}
                                        onMouseLeave={(e) => !isActive && (e.currentTarget.style.backgroundColor = 'transparent')}
                                    >
                                        {m.label}
                                        {isActive && <Check size={14} />}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>

                <div style={styles.divider}></div>

                {/* 2. Year Picker */}
                <div style={{position: 'relative'}} ref={yearRef}>
                    <button 
                        style={{...styles.pickerBtn, minWidth: '90px'}} 
                        onClick={() => { setIsYearOpen(!isYearOpen); setIsMonthOpen(false); }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <span>{selectedYear}</span>
                        <ChevronDown size={14} color="#94a3b8" style={{transform: isYearOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s'}}/>
                    </button>

                    {isYearOpen && (
                        <div style={{...styles.dropdownMenu, width: '100px', left: 'auto', right: 0}}>
                            {years.map((y) => {
                                const isActive = y === selectedYear;
                                return (
                                    <div 
                                        key={y}
                                        style={{...styles.dropdownItem, ...(isActive ? styles.activeItem : {})}}
                                        onClick={() => { setSelectedYear(y); setIsYearOpen(false); }}
                                        onMouseEnter={(e) => !isActive && (e.currentTarget.style.backgroundColor = '#f8fafc')}
                                        onMouseLeave={(e) => !isActive && (e.currentTarget.style.backgroundColor = 'transparent')}
                                    >
                                        {y}
                                        {isActive && <Check size={14} />}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>
            {/* --- END REPLACEMENT --- */}

            <div style={styles.searchContainer}>
                <Search size={16} style={styles.searchIcon} />
                <input 
                    type="text" 
                    placeholder="Search patient or client..." 
                    style={styles.searchInput}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
        </div>
      </header>

      <div style={styles.tableCard}>
        <div style={styles.tableWrapper}>
            <table style={styles.table}>
                <thead style={styles.thead}>
                    <tr>
                        <th style={{...styles.th, width: '50px', textAlign: 'center'}}>#</th>
                        <th style={styles.th}>Client Name</th>
                        <th style={styles.th}>Patient Name</th>
                        <th style={styles.th}>Case Manager</th>
                        <th style={styles.th}>Start Date</th>
                        <th style={{...styles.th, textAlign: 'center'}}>Status</th>
                        <th style={{...styles.th, textAlign: 'center', width: '150px'}}>Action</th>
                    </tr>
                </thead>
                <tbody style={styles.tbody}>
                    {loading ? (
                         <tr><td colSpan={7} style={{padding: '3rem', textAlign: 'center', color: '#64748b'}}>Loading patients...</td></tr>
                    ) : (
                        filteredPatients.map((patient, index) => (
                        <tr 
                            key={patient.id} 
                            style={{...styles.tr, backgroundColor: hoverRowId === patient.id ? '#f8fafc' : 'white'}}
                            onMouseEnter={() => setHoverRowId(patient.id)}
                            onMouseLeave={() => setHoverRowId(null)}
                        >
                            <td style={{...styles.td, textAlign: 'center', backgroundColor: '#f8fafc'}}>{index + 1}</td>
                            <td style={styles.td}>
                                <div style={{fontWeight: 500}}>{patient.client_name_display}</div>
                            </td>
                            
                            <td style={styles.td}>
                                <div style={styles.boldText}>{patient.patient_name}</div>
                                <div style={styles.subText}>{patient.age} / {patient.sex}</div>
                            </td>

                            <td style={styles.td}>{patient.case_manager}</td>
                            <td style={styles.td}>{patient.case_start_date}</td>

                            <td style={{...styles.td, textAlign: 'center'}}>
                                <span style={{...styles.statusPill, ...getStatusStyle(patient.status)}}>
                                    {patient.status}
                                </span>
                            </td>

                            <td style={{...styles.td, textAlign: 'center'}}>
                                <button 
                                    style={styles.sheetBtn} 
                                    onClick={() => handleNavigateToSheet(patient.id)}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#dcfce7'}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                                >
                                    <FileText size={16} /> Open Sheet
                                </button>
                            </td>
                        </tr>
                    )))}
                </tbody>
            </table>
        </div>
      </div>
    </div>
  );
};

export default CaseSheetIndex;